import { inject, Injectable } from '@angular/core';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import {
  ImportData,
  Warning,
  ImportArray,
  ImportDataValidationHints,
  ImportDataValidation,
  DataGroupedByIndicator,
} from '../../models/import-data';
import { GlobalsStore } from '../../stores/globals.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { IndicatorData } from '../../models/indicator-data';
import { CategoryStore } from '../../stores/category/category.state';
import { GeoDataStore } from '../../stores/geo/geo-data.state';
import { KeyValueDistricts } from '../../models/geo-data';
import { DataLayer } from '../../models/data-layer';
import { ComparisonValueStore } from '../../stores/comparison-value/comparison-value.state';
import { isNumber } from 'chart.js/helpers';

@Injectable({
  providedIn: 'root',
})
export class ImportValidatorService {
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly globalStore = inject(GlobalsStore);
  readonly geoDataStore = inject(GeoDataStore);
  readonly comparisonStore = inject(ComparisonValueStore);

  hasEmptyCells = false;

  /**
   * Validates the provided import data against the existing data layers and indicators.
   * It ensures that the input data is properly structured, matches the necessary district keys,
   * and checks for any warnings, errors, or other notifications required to process the data.
   *
   * @param {ImportArray} dataToImport - The array of data to be imported. It includes districts, indicators, and their values.
   * @param {DataLayer} dataLayer - The selected data layer against which the import validation will be performed.
   * @return {ImportDataValidation} Returns an object containing the validation results, including success, warnings, errors, and hints.
   */
  validateImportData(
    dataToImport: ImportArray,
    dataLayer: DataLayer
  ): ImportDataValidation {
    this.hasEmptyCells = false;
    const selectedLayer = dataLayer;
    const comparisonValueKeys = this.comparisonStore.keysWithoutDefaults();
    const geoKeysInLayer = this.geoDataStore.keysFromSelection();
    const districtKeysInLayer = [...geoKeysInLayer, ...comparisonValueKeys].map(
      key => String(key)
    );

    const featuresInLayer: KeyValueDistricts =
      this.geoDataStore.keyValueDistrictsFromSelection();

    const importValidationResult: ImportDataValidation = {
      preview: {
        success: [],
        info: [],
        warning: [],
        error: [],
        lock: [],
        secondary: [],
      },
      hints: {
        noGeoData: false,
        featureKeysNotMatch: false,
        keysNotFound: [],
        missingKeys: [],
        missingFeatures: {},
        featuresToAdd: {},
        hasEmptyCells: false,
      },
      layer: selectedLayer,
    };

    if (!selectedLayer) return importValidationResult;
    const communeDataForLayer: DataGroupedByIndicator =
      this.indicatorDataStore.groupByLayerAndIndicator()[selectedLayer._id] ||
      {};

    const allFeatureKeys = Object.keys(dataToImport.districts);
    const importKeys = allFeatureKeys
      .filter(key => !comparisonValueKeys.includes(key))
      .map(key => String(key));

    const filteredFeatures = importKeys.reduce<KeyValueDistricts>(
      (result, key) => {
        if (!districtKeysInLayer.includes(key)) {
          importValidationResult.hints.keysNotFound.push(key);
          return result;
        }

        if (dataToImport.districts[key] !== undefined) {
          result[key] = dataToImport.districts[key];
        }
        return result;
      },
      {}
    );

    this.checkDistrictKeys(
      filteredFeatures,
      allFeatureKeys,
      importKeys,
      geoKeysInLayer,
      featuresInLayer,
      importValidationResult.hints
    );

    const combinedData: { [key: string]: ImportData } = this.combineImportArray(
      dataToImport,
      filteredFeatures,
      communeDataForLayer,
      selectedLayer._id,
      selectedLayer.parent,
      districtKeysInLayer
    );

    try {
      for (const indicatorKey in combinedData) {
        const importData = combinedData[indicatorKey];
        const indicatorId = importData.indicator;
        const indicatorDataToMerge: IndicatorData | null =
          communeDataForLayer && communeDataForLayer[indicatorId]
            ? communeDataForLayer[indicatorId]
            : null;

        if (
          indicatorId &&
          communeDataForLayer &&
          communeDataForLayer[indicatorId]
        ) {
          importData._id = communeDataForLayer[indicatorId]._id;
        }

        this.validateNumericValues(importData);
        this.checkMergedWarnings(importData, indicatorDataToMerge, 'avg');
        this.checkMergedWarnings(importData, indicatorDataToMerge, 'total');
        this.checkDistrictValues(
          importData,
          indicatorDataToMerge,
          districtKeysInLayer
        );
        this.categorizeImportData(importData, importValidationResult.preview);
      }
      importValidationResult.hints.hasEmptyCells = this.hasEmptyCells;
    } catch (e) {
      console.error(e);
      throw new Error('An error occurred during data import validation.');
    }

    return importValidationResult;
  }

  /**
   * Validates the numeric values in the provided import data object. If the `avg` or `total` property
   * of the object contains a non-numeric value and the `invalid` property is not set to true,
   * the method sets the `invalid` property to true and logs the object.
   *
   * @param {ImportData} importData - The object containing the average and total values to be validated.
   * @return {void} This method does not return a value.
   */
  private validateNumericValues(importData: ImportData): void {
    const avgValue = this.parseExcelValue(importData.avg ?? null);
    const totalValue = this.parseExcelValue(importData.total ?? null);
    const avgInvalid = avgValue === false;
    const totalInvalid = totalValue === false;

    if (!importData.errorKeys) importData.errorKeys = [];
    if (!importData.errorValues) importData.errorValues = {};

    if (avgInvalid) {
      importData.errorValues['avg'] = importData.avg;
      importData.errorKeys.push('avg');
      importData.invalid = true;
    } else {
      importData.avg = avgValue ?? undefined;
    }

    if (totalInvalid) {
      importData.errorValues['total'] = importData.total;
      importData.errorKeys.push('total');
      importData.invalid = true;
    } else {
      importData.total = totalValue ?? undefined;
    }
  }

  /**
   * Checks and merges warnings related to a specific field from the indicatorDataToMerge into the importData object.
   * If the field in importData is missing or undefined but exists in indicatorDataToMerge,
   * a warning is created or updated with the old values.
   *
   * @param {ImportData} importData - The data object to check and potentially update with warning information.
   * @param {any} indicatorDataToMerge - The source data object containing the field to be checked for merging.
   * @param {'avg' | 'total'} field - The field in the data objects to be checked ('avg' or 'total').
   * @return {void} This method does not return a value.
   */
  private checkMergedWarnings(
    importData: ImportData,
    indicatorDataToMerge: any,
    field: 'avg' | 'total'
  ): void {
    const value = importData[field];
    if (
      !value &&
      value !== 0 &&
      indicatorDataToMerge &&
      indicatorDataToMerge[field]
    ) {
      if (!importData.warning) importData.warning = this.getEmptyWarning();
      importData.warning.oldValues[field] = indicatorDataToMerge[field];
      importData.warningKeys.push(field);
    }
  }

  /**
   * Checks and validates the district values within the provided import data and updates
   * or flags warnings and invalid states accordingly.
   *
   * @param {ImportData} importData - The data to be imported, containing district-related values,
   *                                  warnings, and invalid flags.
   * @param {any} indicatorDataToMerge - The reference data to merge or compare district values with.
   * @param {string[]} districtKeysInLayer - An array of district keys that are present in the layer.
   *
   * @return {void} - This method does not return a value. It modifies the importData object
   *                  directly by updating district values, adding warnings, and/or setting invalid flags.
   */
  private checkDistrictValues(
    importData: ImportData,
    indicatorDataToMerge: any,
    districtKeysInLayer: string[]
  ): void {
    for (const districtKey in importData.data) {
      if (!districtKeysInLayer.includes(String(districtKey))) continue;

      const value = this.parseExcelValue(importData.data[districtKey]);

      const valueInvalid = value === false;

      if (!importData.invalid && valueInvalid) importData.invalid = true;

      if (valueInvalid) {
        if (!importData.errorKeys) importData.errorKeys = [];
        if (!importData.errorValues) importData.errorValues = {};
        importData.errorKeys.push(districtKey);
        importData.errorValues[districtKey] = importData.data[districtKey];
        importData.data[districtKey] = null;
      } else {
        importData.data[districtKey] = value;
      }

      if (
        !value &&
        value !== 0 &&
        indicatorDataToMerge &&
        indicatorDataToMerge.data &&
        Object.prototype.hasOwnProperty.call(
          indicatorDataToMerge.data,
          districtKey
        ) &&
        indicatorDataToMerge.data[districtKey]
      ) {
        importData.data[districtKey] = null;
        if (!importData.warning) importData.warning = this.getEmptyWarning();
        importData.warning.oldValues[districtKey] =
          indicatorDataToMerge.data[districtKey];
        importData.warningKeys.push(districtKey);
      }
    }
  }

  /**
   * Converts the value from the Excel sheet to a number.
   * Handles both `.` and `,` as decimal separators.
   * Ensures that the converted value is a valid number.
   *
   * @param value The value from the Excel sheet to be converted.
   * @returns The numeric value or `null` if the conversion fails.
   */
  private parseExcelValue(
    value: string | number | null | undefined
  ): number | false | null {
    if (value === 'k. A.') return null;
    if (value === null || value === undefined || value === '') {
      this.hasEmptyCells = true;
      return null;
    }

    if (typeof value === 'number') {
      // Already a number
      return value;
    }

    // Replace commas with dots (standard decimal separator).
    const normalizedValue = value.replace(',', '.').trim();

    // Check if the string can be converted to a number
    const parsedValue = parseFloat(normalizedValue);

    // Return the parsed value if it's a valid number, otherwise null
    return !isNaN(parsedValue) && isFinite(parsedValue) ? parsedValue : false;
  }

  /**
   * Categorizes the provided import data and pushes it into the corresponding category of the preview object.
   *
   * @param {ImportData} importData - The import data object that needs to be categorized.
   * @param {Object} preview - The object containing arrays for different categories (e.g., lock, error, secondary, warning, info, success) where the categorized data will be stored.
   * @return {void} This method does not return a value; it modifies the preview object directly.
   */
  private categorizeImportData(importData: ImportData, preview: any): void {
    if (importData.isLocked) {
      preview.lock.push(importData);
    } else if (importData.invalid) {
      preview.error.push(importData);
    } else if (!importData.indicator) {
      preview.secondary.push(importData);
    } else if (importData.warning) {
      preview.warning.push(importData);
    } else if (importData._id) {
      preview.info.push(importData);
    } else {
      preview.success.push(importData);
    }
  }

  /**
   * Retrieves an empty warning object.
   *
   * @return {Warning} An object containing an empty set of old values.
   */
  private getEmptyWarning(): Warning {
    return { oldValues: {} };
  }

  /**
   * Combines an import array with additional data, grouping and processing it into a structured format.
   *
   * @param {ImportArray} importArray - The array containing indicators, values, and other related data for import.
   * @param {DataGroupedByIndicator} currentData - Existing data grouped by indicator, used to update or merge with new data.
   * @param {string} dataLayerId - The identifier for the data layer being used in the import or merge operation.
   * @param {string} dataLayerTypeId - The identifier for the type of the data layer being used.
   * @param {string[]} districtKeysInLayer - The list of district keys that exist within the selected data layer.
   * @return { [key: string]: ImportData } A map of indicator keys to their processed and structured import data objects.
   */
  private combineImportArray(
    importArray: ImportArray,
    filteredFeatures: KeyValueDistricts,
    currentData: DataGroupedByIndicator,
    dataLayerId: string,
    dataLayerTypeId: string,
    districtKeysInLayer: string[]
  ): { [key: string]: ImportData } {
    const indicatorData: { [key: string]: ImportData } = {};
    const keyIdList = this.indicatorStore.keyIdList(); // get key id list from db? to get all and identify if indicator has no rights?
    const indicators = this.indicatorStore.items();

    for (let idx = 0; idx < importArray.indicatorKeys.length; idx++) {
      let hasDistrictData = false;
      const key = importArray.indicatorKeys[idx];
      const indicatorId = keyIdList[key];
      const indicator = indicators[indicatorId]; // when not in store but in db - user has no rights to this indicator

      // check indicator exists in db?
      // check workspace and import access?
      const data: ImportData = {
        _id: '',
        indicator: indicatorId,
        key: key,
        importName: importArray.indicators[idx],
        dataLayer: dataLayerId,
        dataLayerType: dataLayerTypeId,
        avg: importArray.avgValues[idx],
        total: importArray.totalValues[idx],
        data: {},
        districts: filteredFeatures,
        errorKeys: [],
        errorValues: {},
        warningKeys: [],
        isLocked:
          indicator &&
          (!!indicator.locked ||
            this.categoryStore.hasLockedParents(indicator.category)),
      };

      if (currentData && currentData[indicatorId]) {
        data._id = currentData[indicatorId]._id;
      }

      for (const districtKey in importArray.values) {
        if (
          this.geoDataStore.selectedId() &&
          !districtKeysInLayer.includes(String(districtKey))
        ) {
          continue;
        }
        const values = importArray.values[districtKey];

        if (values[idx] !== null && !hasDistrictData) hasDistrictData = true;
        data.data[districtKey] = values[idx];
      }

      if (
        hasDistrictData ||
        importArray.avgValues[idx] ||
        importArray.totalValues[idx]
      ) {
        indicatorData[key] = data;
      }
    }

    return indicatorData;
  }

  /**
   * Validates district keys by comparing provided feature keys and layer keys,
   * and determines missing or unmatched keys while preparing hints for validation.
   *
   * @param {string[]} importKeys - A collection of district features represented as key-value pairs.
   * @param {string[]} layerKeys - An array of keys representing the layers to be compared.
   * @param {KeyValueDistricts} featuresInLayer - A collection of existing layer features as key-value pairs.
   * @param {ImportDataValidationHints} hints - A collection of existing layer features as key-value pairs.
   * unmatched features, and additional features to be added.
   */
  private checkDistrictKeys(
    filteredFeatures: KeyValueDistricts,
    allFeatureKeys: string[],
    importKeys: string[],
    layerKeys: string[],
    featuresInLayer: KeyValueDistricts,
    hints: ImportDataValidationHints
  ): void {
    if (!this.geoDataStore.selectedId()) {
      hints.noGeoData = true;
      hints.featuresToAdd = filteredFeatures;
    } else if (importKeys.length !== layerKeys.length) {
      hints.featureKeysNotMatch = true;
      hints.missingKeys = layerKeys.filter(
        item => allFeatureKeys.indexOf(String(item)) < 0
      );
      hints.missingFeatures = hints.missingKeys.reduce(
        (missing: KeyValueDistricts, key: string) => {
          if (featuresInLayer[key]) {
            missing[key] = featuresInLayer[key]; // Add missing district as key-value pair
          }
          return missing;
        },
        {}
      );
    }
  }

  /**
   * Converts an array of ImportData objects to an array of IndicatorData objects.
   *
   * @param {ImportData[]} importData - The array of ImportData objects to be converted.
   * @return {IndicatorData[]} The resulting array of IndicatorData objects after conversion.
   */
  convertToImportDataToIndicatorData(
    importData: ImportData[]
  ): IndicatorData[] {
    return importData.map(data => {
      const indicatorData: IndicatorData = {
        _id: data._id,
        avg: data.avg ?? undefined,
        data: data.data,
        dataLayer: data.dataLayer,
        dataLayerType: data.dataLayerType,
        indicator: data.indicator,
        total: data.total ?? undefined,
      };
      return indicatorData;
    });
  }
}
